/* VoiceSessionProvider — ONE realtime voice session for the whole SPA.

   The session used to live inside whichever agent surface was mounted
   (AgentDock outside an organization console, CopilotPanel inside one), and
   useVoiceSession hangs up on unmount — so "go to the nventr organization,
   open the app, start the research" hung up on NoVA the moment the route
   crossed into the console (2026-10-13). The provider owns the session
   above the routes; the surfaces only register the client-tool handler
   (navigate_app / delegate_to_copilot / approve_plan / stop_run) for the
   place the user is in, and the context (orgId + page) follows the router
   so the model always knows where it is.

   A tool call that lands while no surface is mounted (the console is still
   loading after a navigation) waits a few seconds for the next surface to
   register instead of failing. */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import useVoiceSession from './useVoiceSession';

const Ctx = createContext(null);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function VoiceSessionProvider({ children }) {
  const location = useLocation();
  const orgId = /^\/orgs\/([^/?#]+)/.exec(location.pathname)?.[1] || '';
  const page = `${location.pathname}${location.search || ''}`.slice(0, 300);
  const handlerRef = useRef(null);

  const onClientTool = useCallback(async (name, args) => {
    for (let i = 0; i < 50 && !handlerRef.current; i += 1) await sleep(100); // eslint-disable-line no-await-in-loop
    if (!handlerRef.current) return { error: 'the screen is still opening — wait a moment, then carry on with the request' };
    return handlerRef.current(name, args);
  }, []);

  const voice = useVoiceSession({ onClientTool, context: { orgId, page } });

  // The surface currently on screen owns the client tools; the newest
  // registration wins and unregistering only clears its own handler.
  const register = useCallback((fn) => {
    handlerRef.current = fn;
    return () => { if (handlerRef.current === fn) handlerRef.current = null; };
  }, []);

  const value = useMemo(() => ({ voice, register, orgId }), [voice, register, orgId]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// The shared session plus this surface's client-tool handler (kept current
// through a ref so the handler can close over fresh component state).
export function useVoice(onClientTool) {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useVoice needs <VoiceSessionProvider>');
  const fnRef = useRef(onClientTool);
  fnRef.current = onClientTool;
  const { register } = ctx;
  useEffect(() => register((name, args) => (fnRef.current ? fnRef.current(name, args) : undefined)), [register]);
  return ctx.voice;
}
